// 即時事件（/v1/vms/stream，@kinio/web-sdk VmsStream）：每個 plane 一條連線、訂閱目前範圍內的家戶。
// 給：最近事件（平面圖 / 地圖閃爍、總覽）、感測器即時狀態（門開 / 有人）、事件彈窗；警報處理狀態變更（alarm_state，EVT-06）；
// 在線狀態（presence）與匯出進度（job）——畫面上的在線狀態與工作進度以這兩種幀為準，輪詢只當斷線恢復路徑（SPEC W24）：
// 串流連著時 useHomeDetails / 匯出列表不輪詢（或拉長），斷線重連成功那一刻把家戶 / 設備 / 匯出列表重撈一次補漏。
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { VmsStream, type AlarmStateFrame, type JobFrame, type PresenceState, type StreamEvent, type StreamState } from '@kinio/web-sdk';
import { useScope } from './scope';
import { sensorActive } from './devices';

export interface LiveEvent extends StreamEvent {
  receivedAt: number;
}

/** 串流推來的在線狀態（key `<home_id>/<device_ref>`）；at = 狀態改變的時間（ms）。 */
export interface LivePresence {
  state: PresenceState;
  reason?: string;
  at: number;
}

interface Ctx {
  recent: LiveEvent[];
  presence: Map<string, LivePresence>;
  sensors: Map<string, { active: boolean; ts: number }>;
  state: StreamState;
  subscribe(cb: (e: LiveEvent) => void): () => void;
  /** 警報處理狀態變更（EVT-06） */
  subscribeAlarm(cb: (a: AlarmStateFrame) => void): () => void;
  /** 工作進度（W24；目前只有匯出） */
  subscribeJob(cb: (j: JobFrame) => void): () => void;
}

const LiveCtx = createContext<Ctx>({
  recent: [],
  presence: new Map(),
  sensors: new Map(),
  state: 'idle',
  subscribe: () => () => {},
  subscribeAlarm: () => () => {},
  subscribeJob: () => () => {},
});

/** 斷線恢復：串流重新連上時重撈的查詢（在線狀態、設備、匯出進度）。 */
export const STREAM_RECOVERY_QUERIES = [['home'], ['vms-devices'], ['exports']] as const;

export function LiveEventsProvider({ children }: { children: ReactNode }) {
  const { visible } = useScope();
  const [recent, setRecent] = useState<LiveEvent[]>([]);
  const [sensors, setSensors] = useState(new Map<string, { active: boolean; ts: number }>());
  const [state, setState] = useState<StreamState>('idle');
  const [presence, setPresence] = useState(new Map<string, LivePresence>());
  const subs = useRef(new Set<(e: LiveEvent) => void>());
  const alarmSubs = useRef(new Set<(a: AlarmStateFrame) => void>());
  const jobSubs = useRef(new Set<(j: JobFrame) => void>());
  const qc = useQueryClient();
  const wasOpen = useRef(false);

  const streams = useRef(new Map<string, VmsStream>());

  const byApi = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const h of visible) {
      const api = h.endpoints.api;
      m.set(api, [...(m.get(api) ?? []), h.home_id]);
    }
    return m;
  }, [visible]);

  useEffect(() => {
    const onState = (s: StreamState) => {
      setState(s);
      if (s !== 'open') return;
      // 第一次連上不用（查詢本來就剛載）；之後每次重連成功補一次斷線期間漏掉的
      if (wasOpen.current) for (const k of STREAM_RECOVERY_QUERIES) void qc.invalidateQueries({ queryKey: [...k] });
      wasOpen.current = true;
    };
    for (const [api, homes] of byApi) {
      let s = streams.current.get(api);
      if (!s) {
        s = new VmsStream({
          apiEndpoint: api,
          onState,
          onEvent: (e) => {
            const le: LiveEvent = { ...e, receivedAt: Date.now() };
            setRecent((l) => (l.some((x) => x.event.id === le.event.id) ? l : [le, ...l].slice(0, 200)));
            const act = sensorActive(e.event.type, e.event.payload);
            if (act !== null) setSensors((m) => new Map(m).set(`${e.homeId}/${e.deviceRef}`, { active: act, ts: le.receivedAt }));
            subs.current.forEach((cb) => cb(le));
          },
          onAlarmState: (a) => alarmSubs.current.forEach((cb) => cb(a)),
          onPresence: (p) => {
            const at = Date.parse(p.at);
            setPresence((m) => new Map(m).set(`${p.homeId}/${p.deviceRef}`, { state: p.state, reason: p.reason, at: Number.isFinite(at) ? at : Date.now() }));
          },
          onJob: (j) => jobSubs.current.forEach((cb) => cb(j)),
        });
        streams.current.set(api, s);
        s.start();
      }
      s.subscribe(homes);
    }
    for (const [api, s] of streams.current) {
      if (!byApi.has(api)) {
        s.stop();
        streams.current.delete(api);
      }
    }
  }, [byApi, qc]);

  useEffect(
    () => () => {
      streams.current.forEach((s) => s.stop());
      streams.current.clear();
    },
    [],
  );

  const value = useMemo<Ctx>(
    () => ({
      recent,
      presence,
      sensors,
      state,
      subscribe: (cb) => {
        subs.current.add(cb);
        return () => subs.current.delete(cb);
      },
      subscribeAlarm: (cb) => {
        alarmSubs.current.add(cb);
        return () => alarmSubs.current.delete(cb);
      },
      subscribeJob: (cb) => {
        jobSubs.current.add(cb);
        return () => jobSubs.current.delete(cb);
      },
    }),
    [recent, presence, sensors, state],
  );
  return <LiveCtx.Provider value={value}>{children}</LiveCtx.Provider>;
}

export function useLiveEvents(): Ctx {
  return useContext(LiveCtx);
}
